import { CommonModule } from '@angular/common';
import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';

import { FooterComponent } from '../../../shared/components/footer/footer.component';
import { LoginValidationComponent } from '../../../book-create/login-validation.component/login-validation.component';
import { MenuComponent } from '../../../shared/components/menu/menu.component';
import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { AdminReviewerEditService } from '../../../shared/services/apis/platform/admin-reviewer-edit.service';
import { AdminReviewerBookEditRequest } from '../../../shared/services/requests/platform/admin-reviewer-edit.request';
import { BookVersion } from '../../../shared/services/models/platform/book-version.model';
import { AdminReviewerBookEditResponse } from '../../../shared/services/responses/platform/admin-reviewer-book-edit.response';
import { StatusVersionEnum } from '../../../shared/services/enums/status-version.enum';
import FileSaver from 'file-saver';
import { CategoryService } from '../../../shared/services/apis/platform/category.service';

// ======= NOVO: upload signed URL (Create/PUT/Finalize) =======
import { FileUploadService } from '../../../shared/services/apis/platform/file-upload.service';
import {
  FileUploadCreateResponse,
  FileUploadFinalizeResponse
} from '../../../shared/services/responses/platform/file-upload-create.response';
import { UploadPurposeEnum } from '../../../shared/services/enums/upload-purpose.enum';

type FilesUrls = {
  coverForPrintUrl: string | null;
  bookForPrintUrl: string | null;
  bookForEbookUrl: string | null;
};

// Tipagem local para não depender de você já ter alterado o arquivo do request.
// (Se você já alterou, pode apagar esse type e os casts).
type ReviewerPayloadFileNames = {
  idBookVersion: number;

  isbn: string | null;
  doi: string | null;
  totalPages: number | null;

  frontCoverFileName: string | null;
  backCoverFileName: string | null;

  coverForPrintFileName: string | null;
  bookForPrintFileName: string | null;
  bookForEbookFileName: string | null;
};

@Component({
  selector: 'app-admin-reviewer-edit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    FooterComponent,
    LoginValidationComponent,
    MenuComponent
  ],
  templateUrl: './admin-reviewer-edit.component.html',
  styleUrls: ['./admin-reviewer-edit.component.css'],
  providers: [AdminReviewerEditService]
})
export class AdminReviewerEditComponent implements OnInit {
  private fb = inject(FormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private dialog = inject(MatDialog);
  private alert = inject(AlertService);
  private service = inject(AdminReviewerEditService);
  private categoryService = inject(CategoryService);
  private cdr = inject(ChangeDetectorRef);

  // upload service
  private fileUpload = inject(FileUploadService);

  idBookVersion!: number;
  isLoading = true;
  tried = false;

  response: AdminReviewerBookEditResponse | null | undefined = null;
  bookVersion?: BookVersion | null | undefined = null;

  payload: AdminReviewerBookEditRequest = new AdminReviewerBookEditRequest();

  reviewingStatus: boolean = false;

  // Previews (data URL / URL backend)
  frontCoverPreview: string | null = null;
  backCoverPreview: string | null = null;

  zipCoverFileName: string | null = null;
  zipCoversBase64: string | null = null;

  coverStyleName: string | null = null;
  coverStyleFileName: string | null = null;

  listService: string[] = [];

  authorPhotoUrl: string | null = null;

  primaryCategoryName: string | null = null;
  secondaryCategoryName: string | null = null;

  // Infos básicas
  bookTitle: string | null = null;
  authorName: string | null = null;
  bookAuthorFile: string | null = null;

  // URLs finais vindas do backend (para botão "abrir")
  files: FilesUrls = {
    coverForPrintUrl: null,
    bookForPrintUrl: null,
    bookForEbookUrl: null,
  };

  // Labels (nome do arquivo para exibir na UI)
  coverForPrintLabel: string | null = null;
  bookForPrintLabel: string | null = null;
  bookForEbookLabel: string | null = null;

  // ======= Upload loading =======
  uploadLoading: Record<string, boolean> = {
    frontCover: false,
    backCover: false,
    coverForPrint: false,
    bookForPrint: false,
    bookForEbook: false,
  };

  private static readonly MAX_IMAGE_BYTES = 5 * 1024 * 1024;
  private static readonly MAX_PDF_BYTES = 210 * 1024 * 1024;

  // Form
  form = this.fb.group({
    isbn: ['', [Validators.required, (c: AbstractControl) => this.isbnValidator(c)]],
    doi: [''],
    totalPages: [0, [Validators.required]]
  });

  ngOnInit(): void {
    this.idBookVersion = Number(this.route.snapshot.paramMap.get('idBookVersion'));
    this.fetch();
  }

  // =======================
  // Helpers de payload typed
  // =======================
  private get p(): ReviewerPayloadFileNames {
    // casts para manter o arquivo compilando mesmo se você ainda não alterou o request.
    return this.payload as any as ReviewerPayloadFileNames;
  }

  private setUploadLoading(key: keyof typeof this.uploadLoading, val: boolean) {
    this.uploadLoading[key] = val;
    this.cdr.markForCheck();
  }

  private hasPendingUploads(): boolean {
    return Object.values(this.uploadLoading).some(v => v === true);
  }

  private ensureImageLimit(file: File): boolean {
    if (file.size <= AdminReviewerEditComponent.MAX_IMAGE_BYTES) return true;
    this.alert.show('Imagem muito grande', 'Limite: 5 MB.', AlertType.warning);
    return false;
  }

  private ensurePdfLimit(file: File): boolean {
    if (file.size <= AdminReviewerEditComponent.MAX_PDF_BYTES) return true;
    this.alert.show(
      'PDF muito grande',
      `Arquivo com ${(file.size / (1024 * 1024)).toFixed(1)} MB. Limite: ~210 MB.`,
      AlertType.warning
    );
    return false;
  }

  private async fileToDataUrl(file: File): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('Falha ao ler arquivo'));
      reader.readAsDataURL(file);
    });
  }

  /**
   * Fluxo:
   * 1) FileUpload/Create
   * 2) PUT signed URL (com headers retornados)
   * 3) FileUpload/Finalize -> fileName
   */
  private async uploadByPurpose(file: File, purpose: UploadPurposeEnum): Promise<FileUploadFinalizeResponse> {
    const createRes = await new Promise<FileUploadCreateResponse>((resolve, reject) => {
      this.fileUpload.create({
        originalFileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        purpose: purpose
      }).subscribe({
        next: (x: any) => resolve(x as FileUploadCreateResponse),
        error: (e) => reject(e)
      });
    });

    if (!createRes?.idFileUploadSession || !createRes?.uploadUrl) {
      throw new Error('CreateUpload inválido (sem uploadId/url).');
    }

    const headers = new Headers();

    const extraHeaders: Record<string, string> | null | undefined =
      (createRes as any).headers || (createRes as any)?.Headers || createRes.headers || null;

    if (extraHeaders) {
      for (const k of Object.keys(extraHeaders)) {
        const v = extraHeaders[k];
        if (v != null) headers.set(k, String(v));
      }
    }

    if (!headers.has('Content-Type')) {
      headers.set('Content-Type', file.type || 'application/octet-stream');
    }

    const putResp = await fetch(createRes.uploadUrl, {
      method: 'PUT',
      headers,
      body: file
    });

    if (!putResp.ok) {
      const msg = await putResp.text().catch(() => '');
      throw new Error(`Falha no PUT (${putResp.status}). ${msg || ''}`.trim());
    }

    const fin = await new Promise<FileUploadFinalizeResponse>((resolve, reject) => {
      this.fileUpload.finalize({ idFileUploadSession: createRes.idFileUploadSession }).subscribe({
        next: (x) => resolve(x as FileUploadFinalizeResponse),
        error: (e) => reject(e)
      });
    });

    if (!fin?.fileName) {
      throw new Error('FinalizeUpload inválido (sem fileName).');
    }

    return fin;
  }

  /** Extrai nome amigável de URL */
  getFileNameFromUrl(url?: string | null): string | null {
    if (!url) return null;
    try {
      const clean = url.split('?')[0].split('#')[0];
      const parts = clean.split('/');
      const last = parts[parts.length - 1];
      return decodeURIComponent(last || '').trim() || null;
    } catch {
      return null;
    }
  }

  // =======================
  // Carregamento inicial
  // =======================
  fetch(): void {
    this.isLoading = true;

    this.service.getBookPublishEdit(this.idBookVersion).subscribe({
      next: (res: AdminReviewerBookEditResponse) => {
        if (res.isError) {
          this.alert.show('Erro', res.errorDescription ?? 'Erro ao carregar dados.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
          return;
        }

        this.response = res;
        this.zipCoverFileName = this.response.zipCoversFileName ?? null;
        this.coverStyleName = this.response.coverStyleName ?? null;
        this.coverStyleFileName = this.response.coverStyleFileName ?? null;
        this.zipCoversBase64 = this.response.zipCoversBase64 ?? null;

        this.listService = this.response.listService ?? [];

        this.bookVersion = res.bookVersion ?? null;
        this.authorPhotoUrl = this.bookVersion?.authorPhotoFileName || null;

        if (this.bookVersion) {
          const v = this.bookVersion;

          this.form.patchValue({ isbn: v.isbn || '' });
          this.form.patchValue({ doi: v.doi || '' });
          this.form.patchValue({ totalPages: v.bookEstimatedPages || 0 });

          this.reviewingStatus = v.idStatusVersion == StatusVersionEnum.RevisaoDaEditora;

          // Preview pode vir como URL
          this.frontCoverPreview = v.bookFrontCoverFileName || null;
          this.backCoverPreview = v.bookBackCoverFileName || null;

          this.bookTitle = v.bookTitle || null;
          this.authorName = v.coverAuthorName || null;
          this.bookAuthorFile = v.bookUploadedFileName || null;

          // URLs finais para "abrir"
          this.files.coverForPrintUrl = v.bookAssemblyCoverFileName ?? null;
          this.files.bookForPrintUrl = v.bookPrintFileName ?? null;
          this.files.bookForEbookUrl = v.bookReviewedFileName ?? null;

          // Labels amigáveis
          if (!this.coverForPrintLabel && this.files.coverForPrintUrl) {
            this.coverForPrintLabel = this.getFileNameFromUrl(this.files.coverForPrintUrl) || 'Capa impressão';
          }
          if (!this.bookForPrintLabel && this.files.bookForPrintUrl) {
            this.bookForPrintLabel = this.getFileNameFromUrl(this.files.bookForPrintUrl) || 'Livro impressão';
          }
          if (!this.bookForEbookLabel && this.files.bookForEbookUrl) {
            this.bookForEbookLabel = this.getFileNameFromUrl(this.files.bookForEbookUrl) || 'Livro e-book';
          }

          this.loadCategories();
        }

        this.form.updateValueAndValidity({ emitEvent: false });
        this.isLoading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.alert.show('Erro', 'Erro inesperado ao carregar dados.', AlertType.error);
        this.isLoading = false;
        this.cdr.markForCheck();
      }
    });
  }

  private loadCategories(): void {
    this.categoryService.getCategory().subscribe({
      next: (res) => {
        if (res?.isError) {
          this.alert.show('Erro', res.errorDescription || 'Falha ao carregar categorias.', AlertType.error);
          return;
        }
        this.primaryCategoryName = res.listCategoryStruct?.find(c => c.idCategory === this.bookVersion?.idPrimaryCategory)?.categoryName || null;
        this.secondaryCategoryName = res.listCategoryStruct?.find(c => c.idCategory === this.bookVersion?.idSecondaryCategory)?.categoryName || null;
        this.cdr.markForCheck();
      },
      error: () => this.alert.show('Erro', 'Erro ao carregar categorias.', AlertType.error)
    });
  }

  downloadZipFromServer(): void {
    if (!this.zipCoverFileName || !this.zipCoversBase64) return;
    const bytes = atob(this.zipCoversBase64);
    const arr = new Uint8Array(bytes.length);
    for (let i = 0; i < bytes.length; i++) arr[i] = bytes.charCodeAt(i);
    const blob = new Blob([arr], { type: 'application/zip' });
    FileSaver.saveAs(blob, this.zipCoverFileName);
  }

  // =======================
  // Ações UI
  // =======================
  onLoginValidated(_: any) { /* se precisar */ }

  goBack() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  openBookPreview() {
    const url = this.bookAuthorFile;
    if (url) window.open(url, '_blank');
  }

  openUrlInNewTab(url: string | null, ev?: MouseEvent) {
    ev?.stopPropagation();
    if (url) window.open(url, '_blank');
  }

  // =======================
  // Uploads - CAPAS (imagens)
  // =======================
  async onCoverPicked(ev: Event, which: 'front' | 'back') {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.ensureImageLimit(file)) { input.value = ''; return; }

    const key = which === 'front' ? 'frontCover' : 'backCover';
    this.setUploadLoading(key as any, true);

    try {
      // preview local imediato
      const dataUrl = await this.fileToDataUrl(file);
      if (which === 'front') this.frontCoverPreview = dataUrl;
      else this.backCoverPreview = dataUrl;

      // Ajuste se seu enum tiver purposes específicos para "reviewer"
      const purpose = which === 'front'
        ? UploadPurposeEnum.FrontCover
        : UploadPurposeEnum.BackCover;

      const fin = await this.uploadByPurpose(file, purpose);

      if (which === 'front') this.p.frontCoverFileName = fin.fileName;
      else this.p.backCoverFileName = fin.fileName;

    } catch (e: any) {
      this.alert.show('Erro', e?.message || 'Não foi possível enviar a imagem da capa.', AlertType.error);
    } finally {
      this.setUploadLoading(key as any, false);
      input.value = '';
      this.cdr.markForCheck();
    }
  }

  // =======================
  // Uploads - PDFs
  // =======================
  async onFilePicked(ev: Event, which: 'coverForPrint' | 'bookForPrint' | 'bookForEbook') {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;

    if (!this.ensurePdfLimit(file)) { input.value = ''; return; }

    this.setUploadLoading(which as any, true);

    try {
      const purpose =
        which === 'coverForPrint' ? UploadPurposeEnum.CoverForPrintPdf :
        which === 'bookForPrint' ? UploadPurposeEnum.BookForPrintPdf :
        UploadPurposeEnum.BookForEbookPdf;

      const fin = await this.uploadByPurpose(file, purpose);

      if (which === 'coverForPrint') {
        this.p.coverForPrintFileName = fin.fileName;
        this.coverForPrintLabel = `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
      }
      if (which === 'bookForPrint') {
        this.p.bookForPrintFileName = fin.fileName;
        this.bookForPrintLabel = `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
      }
      if (which === 'bookForEbook') {
        this.p.bookForEbookFileName = fin.fileName;
        this.bookForEbookLabel = `${file.name} (${(file.size / (1024 * 1024)).toFixed(1)} MB)`;
      }

      this.form.updateValueAndValidity({ emitEvent: false });

    } catch (e: any) {
      this.alert.show('Erro', e?.message || 'Não foi possível enviar o arquivo selecionado.', AlertType.error);
    } finally {
      this.setUploadLoading(which as any, false);
      input.value = '';
      this.cdr.markForCheck();
    }
  }

  // =======================
  // Validações
  // =======================
  get canApprove(): boolean {
    if (this.hasPendingUploads()) return false;

    const hasIsbn = this.form.valid;

    const hasFrontCover = !!(this.p.frontCoverFileName || this.frontCoverPreview);
    const hasBackCover = !!(this.p.backCoverFileName || this.backCoverPreview);

    const hasCoverForPrint = !!(this.p.coverForPrintFileName || this.files.coverForPrintUrl);
    const hasBookForPrint = !!(this.p.bookForPrintFileName || this.files.bookForPrintUrl);
    // const hasBookForEbook = !!(this.p.bookForEbookFileName || this.files.bookForEbookUrl);

    return hasIsbn && hasFrontCover && hasBackCover && hasCoverForPrint && hasBookForPrint;
  }

  isbnValidator(ctrl: AbstractControl): ValidationErrors | null {
    const raw = ((ctrl.value as string) || '').replace(/[^0-9Xx]/g, '');
    if (!raw) return { required: true };
    if (!(raw.length === 10 || raw.length === 13)) return { isbn: true };
    return null;
  }

  // =======================
  // Fluxos com MODAIS
  // =======================
  approve(): void {
    this.tried = true;

    if (this.hasPendingUploads()) {
      this.alert.show('Aguarde', 'Existem arquivos ainda sendo enviados. Aguarde terminar antes de aprovar.', AlertType.warning);
      return;
    }

    if (!this.canApprove) {
      this.alert.show('Ops', 'Complete todos os obrigatórios antes de aprovar.', AlertType.warning);
      return;
    }

    const ref = this.dialog.open(ConfirmApproveDialog, { width: '520px', disableClose: true });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.p.idBookVersion = this.idBookVersion;
      this.p.isbn = this.form.value.isbn ?? null;
      this.p.doi = this.form.value.doi ?? null;
      this.p.totalPages = this.form.value.totalPages ?? 0;

      this.isLoading = true;

      this.service.approveReview(this.payload).subscribe({
        next: (saveRes: any) => {
          // se o backend devolver URLs atualizadas
          this.files.coverForPrintUrl = saveRes?.coverForPrintUrl ?? this.files.coverForPrintUrl;
          this.files.bookForPrintUrl = saveRes?.bookForPrintUrl ?? this.files.bookForPrintUrl;
          this.files.bookForEbookUrl = saveRes?.bookForEbookUrl ?? this.files.bookForEbookUrl;

          if (!this.coverForPrintLabel && this.files.coverForPrintUrl) {
            this.coverForPrintLabel = this.getFileNameFromUrl(this.files.coverForPrintUrl) || this.coverForPrintLabel;
          }
          if (!this.bookForPrintLabel && this.files.bookForPrintUrl) {
            this.bookForPrintLabel = this.getFileNameFromUrl(this.files.bookForPrintUrl) || this.bookForPrintLabel;
          }
          if (!this.bookForEbookLabel && this.files.bookForEbookUrl) {
            this.bookForEbookLabel = this.getFileNameFromUrl(this.files.bookForEbookUrl) || this.bookForEbookLabel;
          }

          this.alert.show('Sucesso', 'Revisão aprovada com sucesso.', AlertType.success);
          this.isLoading = false;
          this.cdr.markForCheck();
          this.goBack();
        },
        error: () => {
          this.alert.show('Erro', 'Falha ao aprovar revisão.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  openRejectDialog(): void {
    if (this.hasPendingUploads()) {
      this.alert.show('Aguarde', 'Existem arquivos ainda sendo enviados. Aguarde terminar antes de reprovar.', AlertType.warning);
      return;
    }

    const ref = this.dialog.open(RejectReasonDialog, { width: '520px' });
    ref.afterClosed().subscribe((reason?: string) => {
      if (!reason) return;

      this.isLoading = true;

      this.p.idBookVersion = this.idBookVersion;
      this.p.isbn = this.form.value.isbn ?? null;
      this.p.doi = this.form.value.doi ?? null;
      this.p.totalPages = this.form.value.totalPages ?? 0;

      this.service.rejectReview(this.payload).subscribe({
        next: () => {
          this.alert.show('Sucesso', 'Revisão reprovada e registrada.', AlertType.success);
          this.isLoading = false;
          this.cdr.markForCheck();
          this.goBack();
        },
        error: () => {
          this.alert.show('Erro', 'Falha ao reprovar revisão.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }

  saveForLater(): void {
    if (this.hasPendingUploads()) {
      this.alert.show('Aguarde', 'Existem arquivos ainda sendo enviados. Aguarde terminar antes de salvar.', AlertType.warning);
      return;
    }

    const ref = this.dialog.open(SaveForLaterDialog, { width: '520px', disableClose: true });
    ref.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;

      this.p.idBookVersion = this.idBookVersion;
      this.p.isbn = this.form.value.isbn ?? null;
      this.p.doi = this.form.value.doi ?? null;
      this.p.totalPages = this.form.value.totalPages ?? 0;

      this.isLoading = true;

      this.service.saveForLater(this.payload).subscribe({
        next: (res: any) => {
          this.files.coverForPrintUrl = res?.coverForPrintUrl ?? this.files.coverForPrintUrl;
          this.files.bookForPrintUrl = res?.bookForPrintUrl ?? this.files.bookForPrintUrl;
          this.files.bookForEbookUrl = res?.bookForEbookUrl ?? this.files.bookForEbookUrl;

          this.alert.show('Salvo', 'Alterações salvas. O livro permanece em revisão.', AlertType.success);
          this.isLoading = false;
          this.cdr.markForCheck();
          this.goBack();
        },
        error: () => {
          this.alert.show('Erro', 'Falha ao salvar alterações para continuar mais tarde.', AlertType.error);
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
    });
  }
}

// ===== Dialog: motivo da reprovação =====
@Component({
  selector: 'reject-reason-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule],
  template: `
  <h2 mat-dialog-title>Motivo da reprovação</h2>
  <div mat-dialog-content>
    <mat-form-field appearance="outline" class="w-100">
      <mat-label>Explique o motivo</mat-label>
      <textarea matInput rows="5" [(ngModel)]="reason"></textarea>
    </mat-form-field>
  </div>
  <div mat-dialog-actions style="justify-content: flex-end; gap: 8px;">
    <button mat-stroked-button mat-dialog-close>Cancelar</button>
    <button mat-raised-button color="warn" [mat-dialog-close]="reason" [disabled]="!canSend()">Enviar</button>
  </div>
  `
})
export class RejectReasonDialog {
  reason = '';
  canSend() { return (this.reason || '').trim().length >= 5; }
}

// ===== Dialog: confirmação de aprovação (irreversível) =====
@Component({
  selector: 'confirm-approve-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
  <h2 mat-dialog-title>
    <mat-icon aria-hidden="true">warning_amber</mat-icon>
    Confirmar aprovação
  </h2>

  <div mat-dialog-content>
    <p>
      Esta ação <strong>não é reversível</strong>. Após aprovar a revisão, a versão será liberada e não poderá ser editada.
    </p>
    <p>Deseja realmente aprovar?</p>
  </div>

  <div mat-dialog-actions style="justify-content: flex-end; gap: 8px;">
    <button type="button" mat-stroked-button [mat-dialog-close]="false">Cancelar</button>
    <button type="button" mat-raised-button class="btn-primary button-login" [mat-dialog-close]="true">Aprovar</button>
  </div>
  `
})
export class ConfirmApproveDialog { }

// ===== Dialog: salvar e continuar mais tarde =====
@Component({
  selector: 'save-for-later-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
  <h2 mat-dialog-title>
    <mat-icon aria-hidden="true">save</mat-icon>
    Salvar e continuar mais tarde
  </h2>

  <div mat-dialog-content>
    <p>
      As alterações atuais serão <strong>salvas</strong>, mas o livro continuará no
      <strong>status de revisão</strong>.
    </p>
    <p>Deseja salvar e continuar mais tarde?</p>
  </div>

  <div mat-dialog-actions style="justify-content: flex-end; gap: 8px;">
    <button type="button" mat-stroked-button [mat-dialog-close]="false">Cancelar</button>
    <button type="button" mat-raised-button class="btn-primary button-login" [mat-dialog-close]="true">Salvar</button>
  </div>
  `
})
export class SaveForLaterDialog { }
